import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Info, Heart, Users, Menu, X, DoorOpen, PanelLeftOpen } from 'lucide-react';

export const Layout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [roomsDrawerOpen, setRoomsDrawerOpen] = useState(false);
    const [myRooms, setMyRooms] = useState([]); // [{id, name}]
    const [loadingRooms, setLoadingRooms] = useState(false);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    // Fetch all rooms from localStorage and get their names and members
    useEffect(() => {
        if (!roomsDrawerOpen) return;
        setLoadingRooms(true);
        const keys = Object.keys(localStorage).filter(k => k.startsWith('room_') && k.endsWith('_user'));
        const validKeys = keys.filter(k => {
            try {
                const val = JSON.parse(localStorage.getItem(k));
                return val && val.username && typeof val.username === 'string' && val.username.trim() !== '';
            } catch {
                return false;
            }
        });
        const ids = validKeys.map(k => k.split('_')[1]);
        const myUsernames = Object.fromEntries(validKeys.map(k => [k.split('_')[1], JSON.parse(localStorage.getItem(k)).username]));
        Promise.all(ids.map(async id => {
            try {
                const [roomRes, membersRes] = await Promise.all([
                    fetch(`${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001'}/api/room/${id}`),
                    fetch(`${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001'}/api/room/${id}/members`)
                ]);
                let name = id;
                if (roomRes.ok) {
                    const data = await roomRes.json();
                    name = data.name || id;
                }
                let members = [];
                if (membersRes.ok) {
                    const data = await membersRes.json();
                    members = data.map(m => m.username);
                }
                return { id, name, members };
            } catch {
                return { id, name: id, members: [] };
            }
        })).then(setMyRooms).finally(() => setLoadingRooms(false));
    }, [roomsDrawerOpen]);

    return (
        <div className="min-h-screen flex flex-col">
            <header className="bg-yellow-600 text-white py-4">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center">
                        {/* Left: PanelOpen button + logo/title */}
                        <div className="flex items-center gap-3 min-w-0">
                            <img
                                src="/logo.png"
                                alt="logo"
                                className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0"
                            />
                            <div className="min-w-0">
                                <h1 className="text-lg md:text-2xl font-bold truncate">
                                    Frieten bestellen
                                </h1>
                                <p className="text-yellow-200 text-xs md:text-sm truncate hidden sm:block">
                                    De beste manier om bestellingen te beheren
                                </p>
                            </div>
                        </div>
                        {/* Desktop nav */}
                        <nav className="hidden md:flex space-x-2 items-center">
                            <Link
                                to="/"
                                className={`px-4 py-2 text-sm rounded-md flex items-center gap-2 transition-colors ${
                                    location.pathname === '/'
                                        ? 'bg-yellow-700 text-white'
                                        : 'hover:bg-yellow-700 text-yellow-100'
                                }`}
                            >
                                <Users size={18} />
                                <span className="hidden lg:inline">Nieuw groepje</span>
                                <span className="lg:hidden">Nieuw groepje</span>
                            </Link>
                            {/* Panel open button always visible */}
                            <button
                                className="ml-4 px-4 py-2 text-sm flex items-center gap-2 cursor-pointer hover:bg-yellow-700 text-yellow-100 cursor-pointer rounded-md transition-colors"
                                onClick={() => setRoomsDrawerOpen(true)}
                                aria-label="Open mijn groepjes"
                                type="button"
                            >
                                <PanelLeftOpen size={20} /> Groepjes
                            </button>
                            <Link
                                to="/about"
                                className={`px-4 py-2 text-sm rounded-md flex items-center gap-2 transition-colors ${
                                    location.pathname === '/about'
                                        ? 'bg-yellow-700 text-white'
                                        : 'hover:bg-yellow-700 text-yellow-100'
                                }`}
                            >
                                <Info size={18} /> Over
                            </Link>
                            {/* Ko‑fi as ghost link */}
                            <a
                                href="https://ko-fi.com/devdoobe"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`px-4 py-2 text-sm rounded-md flex items-center gap-3 transition-colors hover:bg-yellow-700 text-yellow-100`}
                            >
                                <Heart size={20} className="text-gray-100"/>
                                Steun op Ko‑fi
                            </a>
                        </nav>
                        {/* Mobile menu toggle */}
                        <div className="flex items-center gap-2 md:hidden">
                            <button
                                onClick={toggleMobileMenu}
                                className="px-2 py-2 text-white hover:bg-yellow-700 rounded-md transition-colors"
                                aria-label="Toggle menu"
                            >
                                {mobileMenuOpen ? <X size={24}/> : <Menu size={24}/>}
                            </button>
                        </div>
                    </div>

                    {/* Mobile nav */}
                    {mobileMenuOpen && (
                        <nav className="md:hidden mt-4 pt-4 border-t border-yellow-500">
                            <div className="flex flex-col space-y-2">
                                <Link
                                    to="/"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`px-4 py-3 text-sm rounded-md flex items-center gap-3 transition-colors ${
                                        location.pathname === '/'
                                            ? 'bg-yellow-700 text-white'
                                            : 'hover:bg-yellow-700 text-yellow-100'
                                    }`}
                                >
                                    <Users size={20} /> Nieuw groepje
                                </Link>
                                {/* Panel open button always visible */}
                                <button
                                    className="px-4 py-2 text-sm flex items-center gap-2 cursor-pointer hover:bg-yellow-700 text-yellow-100 cursor-pointer rounded-md transition-colors"
                                    onClick={() => setRoomsDrawerOpen(true)}
                                    aria-label="Open mijn groepjes"
                                    type="button"
                                >
                                    <PanelLeftOpen size={20} /> Groepjes
                                </button>
                                <Link
                                    to="/about"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`px-4 py-3 text-sm rounded-md flex items-center gap-3 transition-colors ${
                                        location.pathname === '/about'
                                            ? 'bg-yellow-700 text-white'
                                            : 'hover:bg-yellow-700 text-yellow-100'
                                    }`}
                                >
                                    <Info size={20} /> Over
                                </Link>
                                {/* Ko‑fi ghost link on mobile */}
                                <a
                                    href="https://ko-fi.com/devdoobe"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`px-4 py-2 text-sm rounded-md flex items-center gap-3 transition-colors hover:bg-yellow-700 text-yellow-100`}
                                >
                                    <Heart size={20} className="text-gray-100" />
                                    Steun op Ko‑fi
                                </a>
                            </div>
                        </nav>
                    )}
                </div>
            </header>

            {/* Slide-out rooms drawer */}
            {roomsDrawerOpen && (
                <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-40">
                    <div className="w-80 max-w-full h-full bg-white shadow-lg p-6 flex flex-col relative animate-slide-in-right">
                        <button
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
                            onClick={() => setRoomsDrawerOpen(false)}
                            aria-label="Sluit rooms menu"
                        >
                            <X size={28} />
                        </button>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-yellow-700">
                            <Users size={22} /> Mijn groepjes
                        </h2>
                        {loadingRooms ? (
                            <div className="flex items-center gap-2 text-gray-400"><span className="animate-spin">•</span> Laden…</div>
                        ) : myRooms.length === 0 ? (
                            <div className="text-gray-400 text-sm">Geen groepjes gevonden.</div>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {myRooms.map(r => (
                                    <li key={r.id}>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm bg-yellow-100 text-yellow-600 border-2 border-yellow-600 rounded-md hover:bg-yellow-200 transition-colors flex items-start gap-2 cursor-pointer"
                                            onClick={() => { setRoomsDrawerOpen(false); navigate(`/room/${r.id}`); }}
                                        >
                                            <Users size={18} />
                                            <div className="flex flex-col">
                                            {/*<span className="truncate font-semibold">{r.name}</span>*/}
                                            {/* Member summary */}
                                            {r.members && r.members.length > 0 && (
                                                <span className="flex items-center gap-1 flex-wrap">
                                                    {r.members.slice(0,3).map((u, i) => (
                                                        <span key={u} className="px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded-full text-xs flex items-center gap-1">
                                                            <Users size={12} className="inline-block" /> {u}
                                                        </span>
                                                    ))}
                                                    {r.members.length > 3 && (
                                                        <span className="px-2 py-0.5 bg-yellow-300 text-yellow-900 rounded-full text-xs ml-1">+{r.members.length-3}</span>
                                                    )}
                                                </span>
                                            )}
                                            </div>
                                            {/*<span className="ml-auto text-xs text-gray-400">{r.id}</span>*/}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {/* Click outside to close */}
                    <div className="flex-1" onClick={() => setRoomsDrawerOpen(false)} />
                </div>
            )}

            <main className="flex-1 bg-yellow-50 min-h-0">{children}</main>

            <footer className="bg-yellow-600 text-white py-6">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex sm:flex-row flex-col items-center gap-1 sm:gap-2 text-center md:text-left">
                            <img src="/logo.png" alt="logo" className="w-8 h-8"/>
                            <div>
                                <p className="font-medium">Frieten bestellen</p>
                                <p className="text-yellow-200 text-sm">
                                    De beste manier om bestellingen te beheren
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-200">
                            <span className="text-sm">Gemaakt met</span>
                            <Heart size={16} className="text-red-300"/>
                            <span className="text-sm">voor frietjes</span>
                        </div>
                    </div>


                    <div className="mt-4 pt-4 border-t border-yellow-500 text-center flex flex-col items-center gap-2">
                        <p className="mb-2 w-[90%] mx-auto text-xs text-center gap-1 text-yellow-200">
                            Frieten Bestellen is dé manier om samen met vrienden of collega’s eenvoudig je
                            frituurbestelling te organiseren. Maak een groep aan, voeg snacks, frieten, broodjes en
                            dranken toe en houd alles overzichtelijk. Ideaal voor verenigingen, bedrijven en
                            vriendengroepen!
                        </p>
                        <p className="text-yellow-200 text-sm">
                            © 2025 Frieten bestellen. Alle rechten voorbehouden.
                        </p>
                        <Link
                            to="/privacy"
                            className="text-xs text-yellow-100 underline hover:text-white transition-colors"
                        >
                            Privacyverklaring
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};
